const BASE_EXTENSION_URL = 'http://bariendo.com/fhir/extension';

export const FROM_PHONE_NUMBER_COMMUNICATION_EXTENSION = `${BASE_EXTENSION_URL}/from-phone-number`;
export const TO_PHONE_NUMBER_COMMUNICATION_EXTENSION = `${BASE_EXTENSION_URL}/to-phone-number`;
export const TWILIO_CALL_HISTORY_EXTENSION = `${BASE_EXTENSION_URL}/twilio-call-history`;
/** JSON blob: see `TwilioConferenceSession` in `@bariendo/lib/twilio-conference-session`. */
export const TWILIO_CONFERENCE_SESSION_EXTENSION = `${BASE_EXTENSION_URL}/twilio-conference-session`;
export const BINARY_SIGNED_URL_EXTENSION = `${BASE_EXTENSION_URL}/binary-signed-url`;

/**
 * Whether the patient confirmed or declined an appointment by SMS (BAR-2656). Sub-extensions:
 * `status` (`confirmed | declined`), `at` (instant), `via` (`sms`).
 *
 * Deliberately not `Appointment.participant.status`: booking already sets every participant,
 * patient included, to `accepted`, and `patient-access.ts` and `pac-daily-report.ts` both read
 * that — so an SMS confirmation there would be indistinguishable from the default.
 */
export const APPOINTMENT_PATIENT_CONFIRMATION_EXTENSION = `${BASE_EXTENSION_URL}/appointment-patient-confirmation`;
export const APPOINTMENT_PATIENT_CONFIRMATION_STATUS_EXTENSION = 'status';
export const APPOINTMENT_PATIENT_CONFIRMATION_AT_EXTENSION = 'at';
export const APPOINTMENT_PATIENT_CONFIRMATION_VIA_EXTENSION = 'via';

/**
 * The language an automated SMS was sent in (BAR-2656). Stamped on the outbound Communication by
 * the sender, which is the only place that knows: `app_language_code` lives in bariendo-be's MySQL
 * and is never written to `Patient.communication`, so Orbit cannot derive it.
 *
 * Read it back when replying, so an acknowledgement always matches the language of the message it
 * answers rather than guessing.
 */
export const SMS_LANGUAGE_COMMUNICATION_EXTENSION = `${BASE_EXTENSION_URL}/sms-language`;

/**
 * Which Customer.io message produced an SMS (BAR-2715). Sub-extensions, each present only when
 * Customer.io reported it: `campaign-id`, `action-id`, `broadcast-id`, `newsletter-id` (all
 * `valueInteger`). An automation is a `campaign` in Customer.io's API.
 */
export const CUSTOMERIO_MESSAGE_SOURCE_EXTENSION = `${BASE_EXTENSION_URL}/customerio-message-source`;

export const CONTACTPOINT_COMMENT_STRUCTURE_DEFINITION_EXTENSION =
  'http://hl7.org/fhir/extensions/StructureDefinition-contactpoint-comment.html';

export const INCOMING_CALL_ROUTING_HISTORY_EXTENSION = `${BASE_EXTENSION_URL}/incoming-call-routing-history`;

/**
 * One routing attempt that did *not* connect, nested inside
 * {@link INCOMING_CALL_ROUTING_HISTORY_EXTENSION} beside the provider entries.
 *
 * The provider entries record *who* a call was offered to; these record *why* each
 * candidate was passed over — on another call, do-not-disturb, off shift, rang out.
 * Until BAR-2621 that only ever reached the bot log, so no surface could explain a
 * routing decision after the fact.
 *
 * A distinct url rather than more provider entries: readers that walk the container
 * expecting `valueReference` on every child must be able to skip these.
 */
export const INCOMING_CALL_ROUTING_SKIP_EXTENSION = `${INCOMING_CALL_ROUTING_HISTORY_EXTENSION}/skip`;
/** `CallRoutingSkipReason` code for one skipped attempt. */
export const INCOMING_CALL_ROUTING_SKIP_REASON_EXTENSION = `${INCOMING_CALL_ROUTING_SKIP_EXTENSION}/reason`;
/** The provider that was skipped. Absent when a whole rung had nobody to ring. */
export const INCOMING_CALL_ROUTING_SKIP_PROVIDER_EXTENSION = `${INCOMING_CALL_ROUTING_SKIP_EXTENSION}/provider`;
/** Zero-based position in the configured chain the skip happened at. */
export const INCOMING_CALL_ROUTING_SKIP_RUNG_EXTENSION = `${INCOMING_CALL_ROUTING_SKIP_EXTENSION}/rung`;
/** Human-readable description of the rung, e.g. "patient-navigator pool". */
export const INCOMING_CALL_ROUTING_SKIP_TARGET_EXTENSION = `${INCOMING_CALL_ROUTING_SKIP_EXTENSION}/target`;
export const INCOMING_CALL_ROUTING_SKIP_TIMESTAMP_EXTENSION = `${INCOMING_CALL_ROUTING_SKIP_EXTENSION}/timestamp`;

export const RESOURCE_CREATION_DATE_EXTENSION = `${BASE_EXTENSION_URL}/resource-created-at`;

/**
 * When the Patient was created in Medplum, written ONCE (`valueDateTime`) and never
 * re-stamped (BAR-2787) — unlike `RESOURCE_CREATION_DATE_EXTENSION` above, which
 * `setMetaTag` re-stamps on every stage change and so means "when the current
 * stage began".
 *
 * Stamped by the create-only `patient-created-subscription` bot from the created
 * version, and backfilled for older Patients from the oldest history version by
 * `scripts/backfill-patient-created-at.ts`. "Created in Medplum" is not always
 * "signed up": for a Healthie-migrated patient it is the import date. Read it
 * through `readPatientCreatedAt`.
 */
export const PATIENT_CREATED_AT_EXTENSION = `${BASE_EXTENSION_URL}/patient-created-at`;

export const ORGANIZATION_CONTACT_TITLE_EXTENSION = `${BASE_EXTENSION_URL}/organization-contact-title`;
export const ORGANIZATION_CONTACT_PHOTO_EXTENSION = `${BASE_EXTENSION_URL}/organization-contact-photo`;

export const CUSTOM_PERMISSIONS_EXTENSION = `${BASE_EXTENSION_URL}/custom-permissions`;
export const ALL_PATIENTS_ACCESS_EXTENSION = `${BASE_EXTENSION_URL}/all-patients-access`;
/** Opt-in permission to configure the IVR (business hours, menu templates, on-call NP schedule). */
export const IVR_CONFIG_ACCESS_EXTENSION = `${BASE_EXTENSION_URL}/ivr-config-access`;
/**
 * Opt-in permission to view the Patient Access manager dashboard — the daily report
 * over every coordinator's calls.
 *
 * A per-practitioner permission rather than a role code: a PAC manager is a Care
 * Intake coordinator by role, so gating on the role would show the dashboard to
 * the whole team it reports on.
 */
export const PAC_MANAGER_ACCESS_EXTENSION = `${BASE_EXTENSION_URL}/ci-manager-access`;

export const PUSH_NOTIFICATION_SUBSCRIPTION_TOKEN_EXTENSION = `${BASE_EXTENSION_URL}/push-notification-subscription-token`;

export const FORCE_RESOURCE_UPDATE_EXTENSION = `${BASE_EXTENSION_URL}/force-resource-update`;

export const EDITED_COMMUNICATION_PAYLOAD_EXTENSION = `${BASE_EXTENSION_URL}/edited-communication-payload`;

export const BARIATRIC_PROCEDURE_STAGE_EXTENSION = `${BASE_EXTENSION_URL}/bariatric-procedure-stage`;
/** When the stage last changed value (BAR-2595); `valueDateTime`. Patient history holds every transition. */
export const PROCEDURE_STAGE_CHANGED_AT_EXTENSION = `${BASE_EXTENSION_URL}/procedure-stage-changed-at`;

export const EMPLOYER_EXTENSION = `${BASE_EXTENSION_URL}/employer`;

/**
 * The procedure a lead says they want, as distinct from what they bought.
 *
 * A *lead* attribute: present from signup, changed during the sales
 * conversation, editable from the Intake and billing panel. The **package** on a
 * treatment order is the order attribute — read-only, absent until an order
 * exists, and what retail and net price are computed from. When the two diverge
 * (came in wanting IGB, bought ESG) that is the most interesting fact on the
 * panel, which is why both are kept.
 *
 * One of `PROCEDURE_OPTIONS`. Seeded from the intro call's `procedure` answer
 * until the eligibility stream lands, at which point that becomes a third
 * seeder rather than a replacement.
 */
export const INTERESTED_SERVICE_EXTENSION = `${BASE_EXTENSION_URL}/interested-service`;

/**
 * The market a lead is closest to — normally one of the canonical market
 * `Location` names (BAR-2597), though a value retired from that registry is still
 * displayed rather than hidden.
 *
 * **Not the appointment's facility.** The patient list's `Location` column
 * resolves that from `relatedAppointment[0].participant`; this is the market a
 * rep filters on. Seeded from the intro call's `loc` answer, and before any call
 * is charted from the lead's eligibility form.
 */
export const MARKET_LOCATION_EXTENSION = `${BASE_EXTENSION_URL}/market-location`;

export const SLOT_HOLD_EXPIRES_AT_EXTENSION = `${BASE_EXTENSION_URL}/slot-hold-expires-at`;

export const DATA_FROM_HEALTHIE_EXTENSION = `${BASE_EXTENSION_URL}/data-from-healthie`;
export const EDITED_PATIENT_NAME_EXTENSION = `${BASE_EXTENSION_URL}/edited-patient-name`;

export const TIMEZONE_EXTENSION = 'http://hl7.org/fhir/StructureDefinition/timezone';
export const SCHEDULING_PARAMETERS_EXTENSION = 'https://medplum.com/fhir/StructureDefinition/SchedulingParameters';
/** Sub-extension key under SCHEDULING_PARAMETERS_EXTENSION holding the scheduler-facing guideline note. */
export const SCHEDULING_GUIDELINE_PARAMETER = 'scheduling-guideline';
export const TWILIO_CALL_ERROR_EXTENSION = `${BASE_EXTENSION_URL}/twilio-call-error`;
export const TWILIO_AMD_RESULT_EXTENSION = `${BASE_EXTENSION_URL}/twilio-amd-result`;
export const SMS_ERROR_DETAILS_EXTENSION = `${BASE_EXTENSION_URL}/sms-error-details`;

export const QUESTIONNAIRE_PREFILL_EXTENSION = `${BASE_EXTENSION_URL}/questionnaire-prefill`;
export const QUESTIONNAIRE_PREFILL_PATH_EXTENSION = `${QUESTIONNAIRE_PREFILL_EXTENSION}/path`;
export const QUESTIONNAIRE_PREFILL_ANSWER_TYPE_EXTENSION = `${QUESTIONNAIRE_PREFILL_EXTENSION}/answer-type`;

export const QUESTIONNAIRE_RESOURCE_GENERATION_EXTENSION = `${BASE_EXTENSION_URL}/questionnaire-resource-generation`;
export const QUESTIONNAIRE_RESOURCE_TYPE_EXTENSION = `${QUESTIONNAIRE_RESOURCE_GENERATION_EXTENSION}/resource-type`;
export const QUESTIONNAIRE_RESPONSE_RESOURCE_REFERENCE_EXTENSION = `${QUESTIONNAIRE_RESOURCE_GENERATION_EXTENSION}/resource-reference`;

export const QUESTIONNAIRE_REPEAT_GROUP_NUMBERING_EXTENSION = `${BASE_EXTENSION_URL}/questionnaire-repeat-group-numbering`;

export const BARIENDO_TEST_GROUP_ITEM_EXTENSION = 'http://bariendo.com/fhir/extension/test-group-tests';

export const FAX_ORIGINATING_NUMBER = `https://efax.com/originating-fax-number`;

export const APPOINTMENT_COLOR_BACKGROUND_EXTENSION = `${BASE_EXTENSION_URL}/appointment-color-background`;
export const APPOINTMENT_COLOR_BORDER_EXTENSION = `${BASE_EXTENSION_URL}/appointment-color-border`;
export const APPOINTMENT_COLOR_TEXT_EXTENSION = `${BASE_EXTENSION_URL}/appointment-color-text`;
export const APPOINTMENT_CUSTOM_INFO_EXTENSION = `${BASE_EXTENSION_URL}/appointment-custom-info`;

/** Nested: seriesIndex (positive int), seriesTotal (optional int). */
export const APPOINTMENT_SERIES_METADATA_EXTENSION = `${BASE_EXTENSION_URL}/appointment-series-metadata`;

export const DISPLAY_ORDER_EXTENSION = `${BASE_EXTENSION_URL}/display-order`;

export const PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION = `${BASE_EXTENSION_URL}/preferred-pharmacy-template-application`;
export const PREFERRED_PHARMACY_TEMPLATE_LIST_IDENTIFIER_EXTENSION = `${PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION}/list-identifier`;
export const PREFERRED_PHARMACY_TEMPLATE_LIST_VERSION_EXTENSION = `${PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION}/list-version`;
export const PREFERRED_PHARMACY_TEMPLATE_APPLIED_AT_EXTENSION = `${PREFERRED_PHARMACY_TEMPLATE_APPLICATION_EXTENSION}/applied-at`;

export const PREFERRED_PHARMACY_TEMPLATE_VERSION_EXTENSION = `${BASE_EXTENSION_URL}/preferred-pharmacy-template-version`;

export const PATIENT_DUPLICATED_FROM_EXTENSION = `${BASE_EXTENSION_URL}/patient-duplicated-from`;

const IVR_EXTENSION = `${BASE_EXTENSION_URL}/ivr`;

export const IVR_TEMPLATE_NAME_EXTENSION = `${IVR_EXTENSION}/template-name`;
export const IVR_TEMPLATE_DESCRIPTION_EXTENSION = `${IVR_EXTENSION}/template-description`;
export const IVR_AUDIO_FILE_EXTENSION = `${IVR_EXTENSION}/audio-file`;
export const IVR_INVALID_AUDIO_FILE_EXTENSION = `${IVR_EXTENSION}/invalid-audio-file`;

export const IVR_KEY_MAPPING_EXTENSION = `${IVR_EXTENSION}/key-mapping`;
export const IVR_KEY_DIGIT_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/digit`;
export const IVR_ROUTING_ACTION_TYPE_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/action-type`;
export const IVR_ROUTING_TARGET_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/target`;
export const IVR_BACKUP_ROUTING_TARGET_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/backup-target`;
export const IVR_ON_CALL_BACKUP_TARGET_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/on-call-backup-target`;
export const IVR_PLAYBACK_AUDIO_FILE_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/playback-audio-file`;

/**
 * One rung of the routing chain (BAR-2619), repeated in order.
 *
 * Deliberately a *different* URL from `IVR_ROUTING_TARGET_EXTENSION` rather than a reuse of
 * it: a key mapping carries both encodings at once, the rung list for readers that
 * understand chains and the flat action-type/target/backup-target group for readers that
 * do not. Position in `extension[]` is the rung order — FHIR preserves array order, and
 * there is nothing else to sort on.
 */
export const IVR_KEY_RUNG_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/rung`;
export const IVR_RUNG_KIND_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/kind`;
export const IVR_RUNG_ROLE_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/role`;
export const IVR_RUNG_SCOPE_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/scope`;
export const IVR_RUNG_PHONE_NUMBER_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/phone-number`;
export const IVR_RUNG_PROVIDER_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/provider`;
export const IVR_RUNG_AUDIO_FILE_EXTENSION = `${IVR_KEY_RUNG_EXTENSION}/audio-file`;

/**
 * Optional goodbye audio played when every rung has been tried and nobody answered.
 *
 * Distinct from a `play-and-hangup` rung: that one *is* the destination, this one is what
 * the chain running out sounds like. Absent means the call simply ends.
 */
export const IVR_KEY_END_OF_CHAIN_AUDIO_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/end-of-chain-audio`;

/**
 * Set to false to take an option out of the menu without discarding how it was set up.
 *
 * Absent means enabled, so every mapping written before this existed stays live. A bot
 * running an older release ignores the flag and keeps routing the option — it fails toward
 * the previous behaviour rather than toward a caller hitting a dead digit.
 */
export const IVR_KEY_ENABLED_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/enabled`;

/**
 * Names the business-hours set this option is scoped to. Absent means the option
 * routes the same way around the clock.
 */
export const IVR_KEY_HOURS_SET_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/hours-set`;

/**
 * Holds the after-hours routing action as nested action-type/target/backup-target/
 * playback sub-extensions.
 *
 * The in-hours action deliberately stays flat at the top level of the key mapping,
 * exactly where it has always been, so a bot deployed before this change still parses
 * a newly written mapping instead of throwing. A parse failure there is not local: it
 * propagates through parseIvrTemplateBasic to selectIvrTemplate, which returns null and
 * silently drops every caller to direct routing.
 */
export const IVR_KEY_AFTER_HOURS_EXTENSION = `${IVR_KEY_MAPPING_EXTENSION}/after-hours`;

export const IVR_LANGUAGE_SWITCH_KEY_EXTENSION = `${IVR_EXTENSION}/language-switch-key`;
export const IVR_REPLAY_MENU_KEY_EXTENSION = `${IVR_EXTENSION}/replay-menu-key`;

/** Human-readable label for a business-hours set, shown in the config UI. */
export const IVR_HOURS_SET_NAME_EXTENSION = `${IVR_EXTENSION}/hours-set-name`;

/**
 * IANA timezone the set's start/end times are expressed in (e.g. "America/Los_Angeles").
 *
 * Legacy schedules have no timezone and store bare UTC times, which drift by an hour
 * across a DST boundary. When this is present the times are read as wall-clock local to
 * the zone instead, so a set that closes at 17:00 closes at 17:00 all year.
 */
export const IVR_HOURS_TIMEZONE_EXTENSION = `${IVR_EXTENSION}/hours-timezone`;

/** Stores the active IVR template and retry count for an in-progress call. */
export const IVR_PENDING_STATE_EXTENSION = `${IVR_EXTENSION}/pending-state`;

/** Tracks on-call NP dial fallback state for an in-progress call. */
export const IVR_ON_CALL_DIAL_STATE_EXTENSION = `${IVR_EXTENSION}/on-call-dial-state`;

/**
 * @deprecated Tracked async dial fallback state for external-number and specific-provider IVR
 * routing. Superseded by {@link IVR_CHAIN_STATE_EXTENSION}; still read so a call already in
 * flight when BAR-2621 deploys finishes on the mechanism it started on.
 */
export const IVR_DIAL_FALLBACK_STATE_EXTENSION = `${IVR_EXTENSION}/dial-fallback-state`;

/**
 * The resolved routing chain and the walker's cursor for an in-progress IVR call (BAR-2621).
 *
 * The chain is frozen here when the caller presses a key rather than re-resolved on each
 * no-answer: hours sets can close mid-call, and a caller who reached the in-hours chain
 * should finish walking it rather than being handed to a different chain halfway down.
 *
 * Lives on the `Communication` for the same reason the gather retry counter does — Medplum's
 * webhook infrastructure does not forward query parameters to `event.input`.
 */
export const IVR_CHAIN_STATE_EXTENSION = `${IVR_EXTENSION}/chain-state`;

/** Set when call-session recording has been started for an IVR-routed call (avoids duplicate Start Recording). */
export const IVR_CALL_SESSION_RECORDING_STARTED_EXTENSION = `${IVR_EXTENSION}/call-session-recording-started`;

export const ON_CALL_PRIMARY_PRACTITIONER_EXTENSION = `${BASE_EXTENSION_URL}/on-call-primary-practitioner`;
export const ON_CALL_BACKUP_PRACTITIONER_EXTENSION = `${BASE_EXTENSION_URL}/on-call-backup-practitioner`;

/**
 * Marks a Location whose phone numbers are inbound-only.
 *
 * The Location stays `active` so incoming calls to its numbers are recognized as ours, but it is
 * excluded from outbound caller ID / SMS sender selection. Set on the national toll-free number,
 * which reads as spam to patients who expect a local number.
 */
export const LOCATION_INBOUND_ONLY_EXTENSION = `${BASE_EXTENSION_URL}/location-inbound-only`;

/** valueReference: the Practitioner who completed a clinical task (see @bariendo/lib/clinical-tasks). */
export const TASK_COMPLETED_BY_EXTENSION = `${BASE_EXTENSION_URL}/task-completed-by`;

/**
 * Coverage → the EpisodeOfCare it belongs to. Written by bariendo-be
 * (`coverage.constants.ts` EPISODE_OF_CARE_EXTENSION) — keep in sync.
 */
export const COVERAGE_EPISODE_OF_CARE_EXTENSION = `${BASE_EXTENSION_URL}/episode-of-care`;

/** ClaimResponse → the payor's decision, a `BenefitDecision` coding (BAR-2593). */
export const BENEFIT_DECISION_EXTENSION = `${BASE_EXTENSION_URL}/benefit-decision`;

/** ClaimResponse → why a benefit was denied, a `BenefitDenialReason` coding. Denials only. */
export const BENEFIT_DENIAL_REASON_EXTENSION = `${BASE_EXTENSION_URL}/benefit-denial-reason`;

/** Claim / ClaimResponse → the coverage step recorded, a `CoverageAction` coding (BAR-3081). */
export const COVERAGE_ACTION_EXTENSION = `${BASE_EXTENSION_URL}/coverage-action`;

/**
 * Claim / ClaimResponse → a file recorded with the step, a DocumentReference (BAR-3076). Repeats.
 * An extension on both, since ClaimResponse has no supportingInfo.
 */
export const COVERAGE_ACTION_DOCUMENT_EXTENSION = `${BASE_EXTENSION_URL}/coverage-action-document`;

/**
 * Patient → the stored payment status (BAR-3082): sub-extensions `status` (valueCode),
 * `at` (valueDateTime, when it moved there) and `payor` (valueString). Written by the stage bot.
 */
export const PAYMENT_STATUS_EXTENSION = `${BASE_EXTENSION_URL}/payment-status`;
